import { useField } from "formik";
import { useState } from "react";
import { FiChevronDown, FiEye, FiEyeOff } from "react-icons/fi";
import PhoneInput from "react-phone-input-2";

const autocomplete = {
	first_name: "given-name", last_name: "family-name", email: "email",
	username: "username", mobile_number: "tel", dob: "bday", otp: "one-time-code",
};

/** Consistent, persistently labelled inputs for authentication forms. */
const AuthField = ({ name, label, type = "text", helperText, options = [], isLoading,
	autoComplete, placeholder, onChange, onInput, readOnly }) => {
	const [field, meta, helpers] = useField(name);
	const [revealed, setRevealed] = useState(false);
	const invalid = Boolean(meta.touched && meta.error);
	const id = `auth-${name}`;
	const description = [helperText && `${id}-help`, invalid && `${id}-error`].filter(Boolean).join(" ");
	const inputProps = {
		id,
		name,
		"aria-invalid": invalid,
		"aria-describedby": description || undefined,
		"aria-required": typeof label === "string" && label.includes("*"),
		readOnly,
		autoComplete: autoComplete || autocomplete[name] || (type === "password" ? "new-password" : undefined),
	};
	const change = (event) => {
		field.onChange(event);
		onChange?.(event);
	};

	return (
		<div className={`auth-field ${invalid ? "auth-field--invalid" : ""}`}>
			<label htmlFor={id} className="auth-field__label">
				{typeof label === "string" ? label.replace(/\*/g, "") : label}
			</label>
			<div className="auth-field__control">
				{type === "phone" ? (
					<PhoneInput
						country="zw"
						countryCodeEditable={false}
						value={field.value}
						onChange={(value) => helpers.setValue(value ? `+${value.replace(/^\+/, "")}` : "")}
						onBlur={() => helpers.setTouched(true)}
						inputProps={inputProps}
						placeholder="Phone number"
					/>
				) : type === "select" ? (
					<>
						<select {...field} {...inputProps} onChange={change} disabled={isLoading} className="auth-field__input">
							<option value="">{isLoading ? "Loading…" : "Select an option"}</option>
							{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
						</select>
						<FiChevronDown className="auth-field__select-icon" aria-hidden="true" />
					</>
				) : (
					<input
						{...field}
						{...inputProps}
						type={type === "password" && revealed ? "text" : type}
						placeholder={placeholder}
						onChange={change}
						onInput={onInput}
						inputMode={name === "otp" ? "numeric" : undefined}
						className={`auth-field__input ${type === "password" ? "auth-field__input--password" : ""}`}
					/>
				)}
				{type === "password" && (
					<button
						type="button"
						className="auth-field__reveal"
						onClick={() => setRevealed(!revealed)}
						aria-label={`${revealed ? "Hide" : "Show"} ${String(label).replace(/\*/g, "").toLowerCase()}`}
						aria-pressed={revealed}
					>
						{revealed ? <FiEyeOff size={18} /> : <FiEye size={18} />}
					</button>
				)}
			</div>
			{helperText && <div id={`${id}-help`} className="auth-field__hint">{helperText}</div>}
			{invalid && <p id={`${id}-error`} className="auth-field__error" role="alert">{meta.error}</p>}
		</div>
	);
};

export default AuthField;
